import { memo, useMemo, useState, useEffect } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { formatPercent } from "../../utils/formatNumber";

const GEO_URL =
  "https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson";

/* =========================================================
   COLOR SCALE (RELATIVE INTENSITY)
========================================================= */

function getCountryColor(value, max) {
  if (!value || value === 0) return "#e5e7eb";  // light gray for no data
  
  const p = value / max;

  if (p > 0.6) return "#dc2626";   // very high - dark red (hottest)
  if (p > 0.35) return "#f97316";  // high - orange
  if (p > 0.18) return "#fbbf24";  // medium - amber/yellow
  if (p > 0.08) return "#fde047";  // low - bright yellow
  if (p > 0) return "#fef08a";     // very low - pale yellow (more visible)

  return "#e5e7eb";                // no data
}

/* =========================================================
   COMPONENT
========================================================= */

function WorldMap({ countries = [], selectedCountry, onCountrySelect }) {
  const [geoData, setGeoData] = useState(null);

  /* Load GeoJSON */
  useEffect(() => {
    fetch(GEO_URL)
      .then((response) => response.json())
      .then((data) => setGeoData(data))
      .catch((err) => console.error("Failed to load world map:", err));
  }, []);

  /* Create lookup map */
  const countryMap = useMemo(() => {
    const map = new Map();
    countries.forEach((c) => {
      if (c?.key) map.set(c.key, Number(c.value) || 0);
    });
    return map;
  }, [countries]);

  /* max value for relative scale */
  const max = useMemo(() => {
    return Math.max(...countries.map((c) => c.value || 0), 1);
  }, [countries]);

  /* Style function for GeoJSON */
  const styleFeature = (feature) => {
    const name = feature.properties.ADMIN || feature.properties.name;
    const value = countryMap.get(name) || 0;
    const isSelected = name === selectedCountry;

    return {
      fillColor: isSelected ? "#7f1d1d" : getCountryColor(value, max),
      fillOpacity: isSelected ? 0.95 : 0.85,
      color: "#94a3b8",
      weight: 0.7,
    };
  };

  /* Event handlers for GeoJSON */
  const onEachFeature = (feature, layer) => {
    const name = feature.properties.ADMIN || feature.properties.name;
    const value = countryMap.get(name) || 0;

    if (value > 0) {
      layer.on({
        click: () => {
          onCountrySelect?.(name);
        },
        mouseover: (e) => {
          const layer = e.target;
          layer.setStyle({
            fillColor: "#b91c1c",
            fillOpacity: 0.9,
          });
          layer.bindTooltip(
            `<b>${name}</b><br/>Country share: ${formatPercent(value, 1)}`,
            { permanent: false, direction: "top" }
          ).openTooltip();
        },
        mouseout: (e) => {
          const layer = e.target;
          layer.setStyle(styleFeature(feature));
          layer.closeTooltip();
        },
      });
    }
  };

  if (!geoData) {
    return (
      <div style={{ width: "100%", height: 420, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: 420, position: "relative" }}>
      {/* LEGEND */}
      <div
        style={{
          position: "absolute",
          bottom: 14,
          left: 14,
          background: "white",
          padding: "10px 12px",
          borderRadius: 12,
          fontSize: 12,
          boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
          zIndex: 1000,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6, fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif" }}>
          Country intensity
        </div>
        <LegendRow color="#dc2626" label="Very high" />
        <LegendRow color="#f97316" label="High" />
        <LegendRow color="#fbbf24" label="Medium" />
        <LegendRow color="#fde047" label="Low" />
        <LegendRow color="#fef08a" label="Very low" />
        <LegendRow color="#e5e7eb" label="No data" />
      </div>

      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: "100%", width: "100%", borderRadius: 8, userSelect: 'none' }}
        scrollWheelZoom={true}
        zoomControl={true}
        boxZoom={false}
        doubleClickZoom={true}
        dragging={true}
        keyboard={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <GeoJSON
          key={JSON.stringify(countries) + selectedCountry}
          data={geoData}
          style={styleFeature}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
    </div>
  );
}

/* =========================================================
   LEGEND ITEM
========================================================= */

function LegendRow({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      <span>{label}</span>
    </div>
  );
}

export default memo(WorldMap);
